import React from 'react';

/**
 * Deck design-system primitives.
 *
 * Derived from the pitch presentation styling: dark navy surfaces, flat design
 * (no drop shadows), thin pink "eyebrow" accent rules, and rounded panels. Used
 * to give partner-facing surfaces a consistent, modern look. Light mode keeps a
 * clean neutral treatment; dark mode uses the deck navy tokens.
 */

interface AccentBarProps {
  /** Tailwind width class for the bar (e.g. `w-10`, `w-16`). */
  width?: string;
  /** Tailwind height class for the bar (e.g. `h-1`, `h-1.5`). */
  height?: string;
  className?: string;
}

/**
 * Thin solid pink rule used as an "eyebrow" divider above headings and as a
 * top accent on cards.
 */
export const AccentBar: React.FC<AccentBarProps> = ({
  width = 'w-10',
  height = 'h-1',
  className = '',
}) => (
  <span
    aria-hidden
    className={`block ${width} ${height} rounded-full bg-pink-500 ${className}`.trim()}
  />
);

interface PanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Render a pink accent stripe across the top of the panel. */
  accentTop?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * Flat surface for cards and dashboard frames. Relies on fill contrast + a
 * subtle ring rather than shadows. Navy (`deck.panel`) in dark mode.
 */
export const Panel: React.FC<PanelProps> = ({
  accentTop = false,
  className = '',
  children,
  ...rest
}) => (
  <div
    className={`relative rounded-2xl bg-white dark:bg-deck-panel ring-1 ring-gray-100 dark:ring-white/10 ${
      accentTop ? 'overflow-hidden' : ''
    } ${className}`.trim()}
    {...rest}
  >
    {accentTop && (
      <span aria-hidden className="absolute inset-x-0 top-0 h-1 bg-pink-500" />
    )}
    {children}
  </div>
);
